import { getVideosAndTimeRanges } from './utils';
import { getActiveSpeakersWithinTimeRange } from '../../activeSpeaker';
import { Scenes } from '../../../types/Scene';
import { getRecordingSpeakerKey } from '../../../stores/videoSpeakerDetailsStore';
import { getSpeakerDetailsForBroadcast } from '../../../stores/speakerDetailsStore';
import { Clip, LayoutType } from '@/domains/asset';
import { isLayoutAudiogram, isLayoutDefault, isLayoutGrid, isLayoutPip } from '@/libs/layout';
import { getClipBroadcastId, isClipFromRecordingStudio, isCustomUploadClip } from '@/libs/clipUtils';
import { isFallingBetweenRange } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { speakersAnalysisStore } from '@/stores/speakersAnalysis/speakersAnalysis';

interface SpeakerOverlap {
  fromFrame: number;
  toFrame: number;
  videoIds: string[];
  hasScreenshare: boolean;
  isOriginalVideo: boolean;
}

interface FramePointWithLayout {
  frame: number;
  layout: LayoutType;
}

interface ProcessedFramePoints {
  sortedFramePoints: FramePointWithLayout[];
  speakerOverlaps: SpeakerOverlap[];
}

const addFramePointFromSceneOrFallback = (
  time: number,
  fallbackLayout: LayoutType,
  scenes: Scenes,
  fps: number,
  framePointsWithLayout: FramePointWithLayout[]
): FramePointWithLayout[] => {
  if (scenes.length > 0) {
    const scene = scenes.find(scene => scene.fromFrame / fps <= time && time < scene.toFrame / fps);
    if (scene) {
      return [...framePointsWithLayout, { frame: time, layout: scene.details.layout }];
    }
  }
  return [...framePointsWithLayout, { frame: time, layout: fallbackLayout }];
};

export const getSpeakerOverlaps = ({ clip, fps, scenes }: { clip: Clip; fps: number; scenes: Scenes }) => {
  const speakerDetails = getSpeakerDetailsForBroadcast(getClipBroadcastId(clip)!);
  const videosAndTimeRanges = getVideosAndTimeRanges({ clip });

  let framePointsWithLayout: FramePointWithLayout[] = [];

  // Add video range frame points
  videosAndTimeRanges.forEach(video => {
    video.ranges.forEach(range => {
      framePointsWithLayout = addFramePointFromSceneOrFallback(
        range.start,
        clip.asset_metadata.layout,
        scenes,
        fps,
        framePointsWithLayout
      );
      framePointsWithLayout = addFramePointFromSceneOrFallback(
        range.end,
        clip.asset_metadata.layout,
        scenes,
        fps,
        framePointsWithLayout
      );
    });
  });

  // Add frame points from speakersAnalysis for custom uploads (non-full-length)
  if (isCustomUploadClip({ clip })) {
    const speakersAnalysis = speakersAnalysisStore.getSnapshot()[clip.id];
    if (speakersAnalysis?.times && speakersAnalysis.times.length > 0) {
      speakersAnalysis.times.forEach(time => {
        framePointsWithLayout = addFramePointFromSceneOrFallback(
          time,
          clip.asset_metadata.layout,
          scenes,
          fps,
          framePointsWithLayout
        );
      });
    }
  }

  // Add scene frame points
  if (scenes.length > 0) {
    scenes.forEach(scene => {
      framePointsWithLayout = addFramePointsForTimeRange(
        scene.fromFrame / fps,
        scene.toFrame / fps,
        scene.details.layout,
        framePointsWithLayout
      );

      if (
        scene.details.layout === 'SPEAKER' ||
        scene.details.layout === 'AUDIOGRAM' ||
        scene.details.layout === 'FOCUS' ||
        scene.details.layout === 'PIP'
      ) {
        framePointsWithLayout = addFramePointsForActiveSpeakers(
          scene.fromFrame / fps,
          scene.toFrame / fps,
          speakerDetails,
          scene.details.layout,
          framePointsWithLayout
        );
      }
    });
  } else if (clip.asset_metadata.layout !== 'GRID') {
    const sortedFrames = framePointsWithLayout.map(fp => fp.frame).sort((a, b) => a - b);
    for (let i = 0; i < sortedFrames.length - 1; i++) {
      const fromFrame = sortedFrames[i];
      const toFrame = i === sortedFrames.length - 1 ? clip.asset_metadata.duration || 0 : sortedFrames[i + 1];

      framePointsWithLayout = addFramePointsForActiveSpeakers(
        fromFrame,
        toFrame,
        speakerDetails,
        clip.asset_metadata.layout,
        framePointsWithLayout
      );
    }
  }

  const { speakerOverlaps } = processFramePoints(
    framePointsWithLayout,
    clip,
    fps,
    videosAndTimeRanges,
    scenes,
    speakerDetails
  );

  return speakerOverlaps;
};

// Helper functions
const addFramePointsForActiveSpeakers = (
  fromFrame: number,
  toFrame: number,
  speakerDetails: any,
  layout: LayoutType,
  framePointsWithLayout: FramePointWithLayout[]
): FramePointWithLayout[] => {
  const activeSpeakers = getActiveSpeakersWithinTimeRange(fromFrame, toFrame, speakerDetails);
  const newFramePoints = activeSpeakers
    .filter((speaker): speaker is NonNullable<typeof speaker> => speaker !== null)
    .flatMap(speaker => [
      { frame: speaker.start, layout },
      { frame: speaker.end, layout }
    ]);
  return [...framePointsWithLayout, ...newFramePoints];
};

const addFramePointsForTimeRange = (
  fromFrame: number,
  toFrame: number,
  layout: LayoutType,
  framePointsWithLayout: FramePointWithLayout[]
): FramePointWithLayout[] => {
  const newFramePoints = [
    { frame: fromFrame, layout },
    { frame: toFrame, layout }
  ];
  return [...framePointsWithLayout, ...newFramePoints];
};

const sortAndDeduplicateFramePoints = (framePoints: FramePointWithLayout[]): FramePointWithLayout[] => {
  const sortedFrames = framePoints.sort((a, b) => a.frame - b.frame);
  return sortedFrames.reduce((acc, curr, index) => {
    if (index === sortedFrames.length - 1) {
      acc.push(curr);
    } else if (curr.frame !== sortedFrames[index + 1].frame) {
      acc.push(curr);
    }
    return acc;
  }, [] as FramePointWithLayout[]);
};

const getVisibleVideosInTimeRange = (
  fromFrame: number,
  toFrame: number,
  videosAndTimeRanges: ReturnType<typeof getVideosAndTimeRanges>,
  scenes: Scenes,
  clip: Clip,
  fps: number
): typeof videosAndTimeRanges => {
  return videosAndTimeRanges.filter(video => {
    if (!video.ranges.length) {
      return false;
    }

    if (scenes.length > 0) {
      const scene = scenes.find(scene => {
        return fromFrame >= scene.fromFrame / fps && toFrame <= scene.toFrame / fps;
      });
      if (scene && !scene.details.visible_video_ids.includes(video.videoId)) {
        return false;
      }
    } else if (
      clip.asset_metadata.layout !== 'SPEAKER' &&
      clip.asset_metadata.visible_video_ids &&
      !clip.asset_metadata.visible_video_ids.includes(video.videoId)
    ) {
      return false;
    }

    return video.ranges.some(({ start, end }) => start <= fromFrame && end >= toFrame);
  });
};

export const reorderSpeakersByActiveSpeaker = ({
  clip,
  allPresentSpeakers,
  activeSpeakers,
  layout
}: {
  clip: Clip;
  allPresentSpeakers: ReturnType<typeof getVideosAndTimeRanges>;
  activeSpeakers: ReturnType<typeof getActiveSpeakersWithinTimeRange>;
  layout: LayoutType;
}): void => {
  if (layout === 'PIP') {
    // PIP needs 2 active speakers in order: first active speaker/screenshare, then second active speaker
    if (activeSpeakers.length >= 2) {
      const firstActiveSpeaker = activeSpeakers[0];
      const secondActiveSpeaker = activeSpeakers[1];

      // Reorder to put first active speaker first, second active speaker second
      const speakerIdsOfAllPresent = allPresentSpeakers.map(s =>
        getRecordingSpeakerKey({ broadcastId: getClipBroadcastId(clip)!, recordingId: s.videoId })
      );

      // Move first active speaker to front
      const firstSpeakerIndex = speakerIdsOfAllPresent.indexOf(firstActiveSpeaker.speakerId);
      if (firstSpeakerIndex !== -1) {
        const firstSpeaker = allPresentSpeakers.splice(firstSpeakerIndex, 1)[0];
        allPresentSpeakers.unshift(firstSpeaker);
      }

      // Move second active speaker to second position
      const secondSpeakerIndex = speakerIdsOfAllPresent.indexOf(secondActiveSpeaker.speakerId);
      if (secondSpeakerIndex !== -1 && secondSpeakerIndex > 0) {
        const secondSpeaker = allPresentSpeakers.splice(secondSpeakerIndex, 1)[0];
        allPresentSpeakers.splice(1, 0, secondSpeaker);
      }
    } else if (activeSpeakers.length === 1) {
      // If only one active speaker, still reorder to put them first
      const currentActiveSpeaker = activeSpeakers[0];
      const speakerIdsOfAllPresent = allPresentSpeakers.map(s =>
        getRecordingSpeakerKey({ broadcastId: getClipBroadcastId(clip)!, recordingId: s.videoId })
      );
      const activeSpeakerIndex = speakerIdsOfAllPresent.indexOf(currentActiveSpeaker.speakerId);

      if (activeSpeakerIndex !== -1) {
        const activeSpeaker = allPresentSpeakers.splice(activeSpeakerIndex, 1)[0];
        allPresentSpeakers.unshift(activeSpeaker);
      }
    }
  } else {
    // Existing logic for other layouts (SPEAKER, AUDIOGRAM, etc.)
    const currentActiveSpeaker = activeSpeakers[0];
    if (currentActiveSpeaker) {
      const speakerIdsOfAllPresent = allPresentSpeakers.map(s =>
        getRecordingSpeakerKey({ broadcastId: getClipBroadcastId(clip)!, recordingId: s.videoId })
      );
      allPresentSpeakers.unshift(
        allPresentSpeakers.splice(speakerIdsOfAllPresent.indexOf(currentActiveSpeaker.speakerId), 1)[0]
      );
    }
  }
};

const filterSpeakerVideos = ({
  allPresentSpeakers,
  isFirstScreenshare
}: {
  allPresentSpeakers: ReturnType<typeof getVisibleVideosInTimeRange>;
  isFirstScreenshare: boolean;
}) => {
  return allPresentSpeakers.filter((speakerVideo, index) => {
    if (isFirstScreenshare) {
      return index < 9;
    }
    return index < 8;
  });
};

const processFramePoints = (
  framePoints: FramePointWithLayout[],
  clip: Clip,
  fps: number,
  videosAndTimeRanges: ReturnType<typeof getVideosAndTimeRanges>,
  scenes: Scenes,
  speakerDetails: any
): ProcessedFramePoints => {
  let finalFramePoints = framePoints;

  if (!isClipFromRecordingStudio(clip)) {
    const defaultLayoutScenes = scenes.filter(scene => isLayoutDefault(scene.details.layout));

    const defaultSceneTimespans = defaultLayoutScenes.map(scene => ({
      start: scene.fromFrame / fps,
      end: scene.toFrame / fps
    }));

    const framePointsOutsideDefaultWindows = framePoints.filter(
      fp => !isFallingBetweenRange(defaultSceneTimespans, fp.frame)
    );

    finalFramePoints = framePointsOutsideDefaultWindows;
  }
  const sortedFramePoints = sortAndDeduplicateFramePoints(finalFramePoints);
  const speakerOverlaps: SpeakerOverlap[] = [];

  const individualVideos = videosAndTimeRanges.filter(v => !v.isOriginalVideo); // for speaker layouts and audiogram
  const originalVideos = videosAndTimeRanges.filter(v => v.isOriginalVideo); // for default layout

  for (let i = 0; i < sortedFramePoints.length - 1; i++) {
    const fromFrame = sortedFramePoints[i].frame;
    const toFrame =
      i === sortedFramePoints.length - 1 ? clip.asset_metadata.duration || 0 : sortedFramePoints[i + 1].frame;
    const currentLayout = sortedFramePoints[i].layout;

    if (isLayoutDefault(currentLayout)) {
      speakerOverlaps.push({
        fromFrame: fromFrame * fps,
        toFrame: toFrame * fps,
        videoIds: originalVideos.map(v => v.videoId),
        hasScreenshare: false,
        isOriginalVideo: true
      });
      continue;
    }

    const activeSpeakers = getActiveSpeakersWithinTimeRange(fromFrame, toFrame, speakerDetails);
    const allPresentSpeakers = getVisibleVideosInTimeRange(fromFrame, toFrame, individualVideos, scenes, clip, fps);
    const isFirstScreenshare = !!allPresentSpeakers[0]?.isScreenshare;

    if (
      allPresentSpeakers.length > 1 &&
      (isLayoutAudiogram(currentLayout) ||
        ((!isLayoutGrid(currentLayout) || isLayoutPip(currentLayout)) && !isFirstScreenshare))
    ) {
      reorderSpeakersByActiveSpeaker({
        allPresentSpeakers,
        activeSpeakers,
        layout: currentLayout,
        clip
      });
    }

    speakerOverlaps.push({
      fromFrame: fromFrame * fps,
      toFrame: toFrame * fps,
      videoIds: filterSpeakerVideos({ allPresentSpeakers, isFirstScreenshare }).map(s => s.videoId),
      hasScreenshare: isFirstScreenshare,
      isOriginalVideo: false
    });
  }

  return { sortedFramePoints, speakerOverlaps };
};
